import {
  Badge,
  Box,
  Button,
  ExternalLinkIcon,
  Heading,
  Link,
  PettyCashBudgetIcon,
  Text,
} from '@razorpay/blade/components';
import permissions from 'constants/permissions';
import React, { useState } from 'react';
import { getStaticMediaUrl } from 'utils/Urls';
import useHasPermission from 'utils/useHasPermission';
import noComponents from '../../../assets/noComponents.svg';
import ComponentContainer from '../components/generics/ComponentContainer';
import ComponentHeader from '../components/generics/ComponentHeader';
import ComponentSection from '../components/generics/ComponentSection';
import ComponentSectionItem from '../components/generics/ComponentSectionItem';
import DeductionsBreakdownTable from '../components/generics/DeductionsBreakdownTable';
import useVPF from '../hooks/VPF/useVPF';
import { DeductionData } from '../types';
import VPFModal from '../VPF/VPFModal';
import VPFOptOutModal from '../VPF/VPFOptOutModal';
import { VPFButtons } from '../VPF/VPFButtons';
import IsFeatureDisabledForRearch from 'access/IsFeatureDisabledForRearch';
import { links } from '../constants';

const RecurringDeductionsBreakdown = ({
  data,
  setSelectedComponentId,
  openDeductionModal,
  openDeleteModal,
  isAddDeductionAllowed,
  isLoading,
  isPFEnabled = true,
}: {
  data: DeductionData[];
  setSelectedComponentId: (id: string) => void;
  openDeductionModal: () => void;
  openDeleteModal: () => void;
  isAddDeductionAllowed?: boolean;
  isLoading?: boolean;
  isPFEnabled?: boolean;
}) => {
  const hasPayrollEditPermission = useHasPermission([permissions.PAYROLL_EDIT]);
  const [isVPFModalOpen, setIsVPFModalOpen] = useState(false);
  const [isVPFOptOutModalOpen, setIsVPFOptOutModalOpen] = useState(false);
  const { vpfNode, isVPFActive, isVPFDisabled, shouldManageVPF, onVPFOptIn, onVPFOptOut } =
    useVPF();

  const openVPFModal = () => setIsVPFModalOpen(true);
  const closeVPFModal = () => setIsVPFModalOpen(false);
  const openVPFOptOutModal = () => setIsVPFOptOutModalOpen(true);
  const closeVPFOptOutModal = () => setIsVPFOptOutModalOpen(false);

  const onAddNewDeduction = () => {
    openDeductionModal();
  };

  const onDelete = (id: string) => {
    setSelectedComponentId(id);
    openDeleteModal();
  };

  const onEdit = (id: string) => {
    setSelectedComponentId(id);
    openDeductionModal();
  };

  const nodes = data.map((item) => ({
    id: item.componentId,
    componentName: item.componentName || '',
    monthlyAmount: Number(item.monthlyAmount),
  }));
  const isDeductionsEmpty = nodes.length === 0;
  const showNewDeductionButton =
    isAddDeductionAllowed && hasPayrollEditPermission && !(!isLoading && isDeductionsEmpty);

  return (
    <>
      <ComponentContainer>
        <ComponentSection>
          <ComponentHeader
            icon={<PettyCashBudgetIcon size="medium" color="surface.icon.primary.normal" />}
            iconBackground={'surface.background.primary.subtle'}
            title={'Recurring Deductions'}
            description={'Monthly deductions which are not part of CTC'}
            button={
              showNewDeductionButton ? (
                <Button variant="tertiary" marginLeft={'auto'} onClick={onAddNewDeduction}>
                  + New Deduction
                </Button>
              ) : null
            }
          />
          {!isDeductionsEmpty && (
            <ComponentSectionItem>
              <DeductionsBreakdownTable
                data={nodes}
                onEdit={onEdit}
                onDelete={onDelete}
                showActions={hasPayrollEditPermission}
                isLoading={isLoading}
                isRefreshing={isLoading && !isDeductionsEmpty}
              />
            </ComponentSectionItem>
          )}
          {!isLoading && isDeductionsEmpty && (
            <ComponentSectionItem>
              <Box
                display="flex"
                flexDirection={'row'}
                gap="spacing.8"
                height="100%"
                justifyContent={'center'}
                alignItems={'center'}>
                <img src={getStaticMediaUrl(noComponents)} alt="no-recurring-deductions" />
                <Box display={'flex'} flexDirection={'column'} gap={'spacing.7'}>
                  <Heading weight="semibold">No recurring deductions added</Heading>
                  <Box
                    display={'flex'}
                    flexDirection={'row'}
                    gap={'spacing.5'}
                    alignItems={'center'}
                    justifyContent={'center'}>
                    {hasPayrollEditPermission ? (
                      <Button onClick={onAddNewDeduction}>+ Add Deduction</Button>
                    ) : null}
                    <Link
                      onClick={() => {}}
                      size="small"
                      icon={ExternalLinkIcon}
                      iconPosition="right">
                      Learn More
                    </Link>
                  </Box>
                </Box>
              </Box>
            </ComponentSectionItem>
          )}
        </ComponentSection>

        {/* VPF */}
        {isPFEnabled && (
          <IsFeatureDisabledForRearch feature="isM3S1Disabled">
            <ComponentSection>
              <ComponentHeader
                icon={<PettyCashBudgetIcon size="medium" color="surface.icon.primary.normal" />}
                iconBackground={'surface.background.primary.subtle'}
                title={'Voluntary PF'}
                description={
                  <Box
                    display={'flex'}
                    flexDirection={'row'}
                    gap={'spacing.2'}
                    alignItems={'baseline'}>
                    <Text size="small" weight="regular" color="surface.text.gray.muted">
                      Voluntarily contribute more to your PF to maximise your savings
                    </Text>
                    <Link
                      onClick={() => window.open(links.learnMoreAboutVpf, '_blank')}
                      size="small">
                      Learn More
                    </Link>
                  </Box>
                }
                titleSuffix={
                  <Badge color={isVPFActive ? 'positive' : 'negative'}>
                    {isVPFActive ? 'Opted In' : 'Opted Out'}
                  </Badge>
                }
                button={
                  <Box display={'flex'} flexDirection={'row'} marginLeft={'auto'}>
                    <VPFButtons
                      shouldManageVPF={Boolean(shouldManageVPF)}
                      isVPFDisabled={isVPFDisabled}
                      onVPFOptIn={openVPFModal}
                      onVPFOptOut={openVPFOptOutModal}
                    />
                  </Box>
                }
              />
              <ComponentSectionItem>
                <DeductionsBreakdownTable
                  data={[vpfNode]}
                  showActions={false}
                  isLoading={isLoading}
                  isRefreshing={isLoading && !isDeductionsEmpty}
                />
              </ComponentSectionItem>
            </ComponentSection>
          </IsFeatureDisabledForRearch>
        )}
      </ComponentContainer>

      <IsFeatureDisabledForRearch feature="isM3S1Disabled">
        <VPFModal
          isOpen={isVPFModalOpen}
          onDismiss={closeVPFModal}
          onOptIn={onVPFOptIn}
          isEdit={isVPFActive}
          onModify={onVPFOptIn}
        />

        <VPFOptOutModal
          isOpen={isVPFOptOutModalOpen}
          onDismiss={closeVPFOptOutModal}
          onOptOut={onVPFOptOut}
        />
      </IsFeatureDisabledForRearch>
    </>
  );
};

export default RecurringDeductionsBreakdown;
